import { cn } from "@/lib/utils";
import { Editor } from "@tiptap/react";
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Bold,
    Code,
    Heading1,
    Heading2,
    Heading3,
    Italic,
    Link as LinkIcon,
    List,
    ListOrdered,
    Quote,
    Strikethrough,
    Underline,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import EmojiPicker from "../EmojiPicker";
import { Button } from "../ui/button";
import LinkInputDialog from "../ui/link-input-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";

interface RichTextToolbarProps {
    editor: Editor;
    className?: string;
}

const COLORS = [
    { value: "#000000", label: "Black" },
    { value: "#4B5563", label: "Gray" },
    { value: "#EF4444", label: "Red" },
    { value: "#F59E0B", label: "Orange" },
    { value: "#10B981", label: "Green" },
    { value: "#3B82F6", label: "Blue" },
    { value: "#8B5CF6", label: "Purple" },
];

const DEFAULT_FONT_FAMILY = "Source Sans 3";

const FONT_FAMILIES = [
    { value: DEFAULT_FONT_FAMILY, label: "Source Sans" },
    { value: "Ubuntu", label: "Ubuntu" },
    { value: "Arial", label: "Arial" },
    { value: "Times New Roman", label: "Times New Roman" },
    { value: "Courier New", label: "Courier New" },
];

const RichTextToolbar = ({ editor, className }: RichTextToolbarProps) => {
    const { t } = useTranslation();
    const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

    const handleLinkAdd = (url: string) => {
        const { from, to } = editor.state.selection;
        const chain = editor.chain().focus().setLink({ href: url });
        // When a range was linked, drop the cursor past the link and clear the
        // stored mark so text typed afterwards is not swallowed into the <a>.
        if (from !== to) {
            chain.setTextSelection(to).unsetMark("link");
        }
        chain.run();
    };

    const toolbarItems = [
        {
            id: 1,
            icon: Bold,
            isActive: () => editor.isActive("bold"),
            onClick: () => editor.chain().focus().toggleBold().run(),
        },
        {
            id: 2,
            icon: Italic,
            isActive: () => editor.isActive("italic"),
            onClick: () => editor.chain().focus().toggleItalic().run(),
        },
        {
            id: 3,
            icon: Underline,
            isActive: () => editor.isActive("underline"),
            onClick: () => editor.chain().focus().toggleUnderline().run(),
        },
        {
            id: 4,
            icon: Strikethrough,
            isActive: () => editor.isActive("strike"),
            onClick: () => editor.chain().focus().toggleStrike().run(),
        },
        {
            id: 5,
            icon: Code,
            isActive: () => editor.isActive("code"),
            onClick: () => editor.chain().focus().toggleCode().run(),
        },
        {
            id: 6,
            icon: Heading1,
            isActive: () => editor.isActive("heading", { level: 1 }),
            onClick: () =>
                editor.chain().focus().toggleHeading({ level: 1 }).run(),
        },
        {
            id: 7,
            icon: Heading2,
            isActive: () => editor.isActive("heading", { level: 2 }),
            onClick: () =>
                editor.chain().focus().toggleHeading({ level: 2 }).run(),
        },
        {
            id: 8,
            icon: Heading3,
            isActive: () => editor.isActive("heading", { level: 3 }),
            onClick: () =>
                editor.chain().focus().toggleHeading({ level: 3 }).run(),
        },
        {
            id: 9,
            icon: Quote,
            isActive: () => editor.isActive("blockquote"),
            onClick: () => editor.chain().focus().toggleBlockquote().run(),
        },
        {
            id: 10,
            icon: List,
            isActive: () => editor.isActive("bulletList"),
            onClick: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
            id: 11,
            icon: ListOrdered,
            isActive: () => editor.isActive("orderedList"),
            onClick: () => editor.chain().focus().toggleOrderedList().run(),
        },
        {
            id: 12,
            icon: LinkIcon,
            isActive: () => editor.isActive("link"),
            onClick: () => {
                // Check if there's a link at the current selection
                if (editor.isActive("link")) {
                    // Remove the link from the selected text
                    editor.chain().focus().unsetLink().run();
                } else {
                    // Check if there's selected text
                    const { from, to } = editor.state.selection;
                    if (from === to) {
                        // No text selected, show dialog to enter both text and URL
                        setIsLinkDialogOpen(true);
                    } else {
                        // Text is selected, show dialog to enter URL
                        setIsLinkDialogOpen(true);
                    }
                }
            },
        },
        {
            id: 13,
            icon: AlignLeft,
            isActive: () => editor.isActive({ textAlign: "left" }),
            onClick: () => editor.chain().focus().setTextAlign("left").run(),
        },
        {
            id: 14,
            icon: AlignCenter,
            isActive: () => editor.isActive({ textAlign: "center" }),
            onClick: () => editor.chain().focus().setTextAlign("center").run(),
        },
        {
            id: 15,
            icon: AlignRight,
            isActive: () => editor.isActive({ textAlign: "right" }),
            onClick: () => editor.chain().focus().setTextAlign("right").run(),
        },
        {
            id: 16,
            icon: null,
            isActive: () => false,
            onClick: () => {},
            customComponent: (
                <EmojiPicker className="h-8 w-8 p-0" editor={editor} />
            ),
        },
    ];

    return (
        <>
            <div
                className={cn(
                    "flex h-10 w-fit flex-wrap items-center justify-center gap-1 overflow-hidden rounded-xl border pl-2 pr-4",
                    className,
                )}
            >
                {toolbarItems.map((item) => (
                    <Fragment key={item.id}>
                        {item.customComponent || (
                            <Button
                                className={cn(
                                    item.isActive() &&
                                        "bg-surface-secondaryHover",
                                )}
                                onClick={item.onClick}
                                size="sm"
                                variant="ghost"
                            >
                                {item.icon && <item.icon className="h-4 w-4" />}
                            </Button>
                        )}
                    </Fragment>
                ))}

                <div className="flex items-center gap-1">
                    <Select
                        defaultValue={DEFAULT_FONT_FAMILY}
                        onValueChange={(value) => {
                            editor.chain().focus().setFontFamily(value).run();
                        }}
                    >
                        <SelectTrigger className="h-8 w-[160px]">
                            <SelectValue
                                placeholder={t("richTextEditor.font")}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {FONT_FAMILIES.map((font) => (
                                <SelectItem
                                    key={font.value}
                                    style={{ fontFamily: font.value }}
                                    value={font.value}
                                >
                                    {t(
                                        `richTextEditor.fonts.${font.value.toLowerCase().replace(/\s+/g, "")}`,
                                    )}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        onValueChange={(value) => {
                            editor
                                .chain()
                                .focus()
                                .setMark("textStyle", { color: value })
                                .run();
                        }}
                    >
                        <SelectTrigger className="h-8 w-[100px]">
                            <SelectValue
                                placeholder={t("richTextEditor.color")}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {COLORS.map((color) => (
                                <SelectItem
                                    key={color.value}
                                    value={color.value}
                                >
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="h-4 w-4 rounded-full border"
                                            style={{
                                                backgroundColor: color.value,
                                            }}
                                        />
                                        {t(
                                            `richTextEditor.colors.${color.label.toLowerCase()}`,
                                        )}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <LinkInputDialog
                isOpen={isLinkDialogOpen}
                onClose={() => setIsLinkDialogOpen(false)}
                onConfirm={handleLinkAdd}
            />
        </>
    );
};

export default RichTextToolbar;
